import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Grid, CircularProgress, Container, Typography, Alert, Button } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { toggleFavorite } from '../../actions/users';
import { startChat } from '../../actions/chat';
import { getPublicPageDetail } from '../../actions/profile';
import { urlImg, isEmpty } from '../../components/common/helpers';
import { useIrAEntrar } from '../../utils/destinoAlEntrar';

import PageBanner from '../../components/services/page/PageBanner';
import PageSummary from '../../components/services/page/PageSummary';
import PageInfoBar from '../../components/services/page/PageInfoBar';
import PageAbout from '../../components/services/page/PageAbout';
import PageLocation from '../../components/services/page/PageLocation';
import PageComments from '../../components/services/page/PageComments';
import PageServiceList from '../../components/services/page/PageServiceList';

const PublicPageDetail = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);

  const categories = useSelector(state => state.cat.categories);
  const favorites = useSelector(state => state.user.user.favorites || []);
  const isAuthenticated = useSelector(state => state.user.loading === false && state.user.user._id);
  const authUser = useSelector(state => state.user.user);

  const isFavorite = useMemo(() => {
    return favorites.some(favId => favId.toString() === pageId.toString());
  }, [favorites, pageId]);

  // Sin sesión, el corazón y «Cotizar» mandan a entrar; al entrar regresa a esta página
  const irAEntrar = useIrAEntrar();

  const onToggleFavorite = () => {
    if (!isAuthenticated) {
      irAEntrar();
      return;
    }
    dispatch(toggleFavorite(pageId));
  };

  const handleQuote = () => {
    console.log('--- Debug Cotizar (PageSummary) ---');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('ownerUser:', ownerUser?._id);
    console.log('isOwner:', isOwner);
    console.log('pageId:', pageId);

    if (!isAuthenticated) {
      console.warn('Usuario no autenticado');
      irAEntrar();
      return;
    }
    if (ownerUser?._id && !isOwner) {
      console.log('Iniciando chat desde PageSummary...');
      const initialMsg = `Hola, me interesa contratar un servicio personalizado. Vi tu perfil de "${page?.nombre}" y me gustaría cotizar. ¿Podrías darme un presupuesto?`;
      dispatch(startChat(ownerUser._id, pageId, 'page', initialMsg, `${ownerUser._id}_${pageId}`));
    } else {
      console.warn('No se puede iniciar chat: falta ownerUser o es el dueño');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchData = async () => {
      try {
        const res = await getPublicPageDetail(pageId);
        if (res && res.page) {
          setData(res);
        } else {
          setError(true);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pageId]);

  const categoriaName = useMemo(() => {
    if (!data?.page?.categoria) return "";
    // nombre resuelto por el backend (funciona aunque la categoría esté deshabilitada)
    if (data.page.categoriaNombre) return data.page.categoriaNombre;
    const found = categories?.find(cat => cat._id === data.page.categoria);
    return found ? found.name : "";
  }, [data?.page?.categoria, data?.page?.categoriaNombre, categories]);

  const addressInfo = data?.address || null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ textAlign: 'center', mt: 10 }}>
        <Typography variant="h5">Página no encontrada</Typography>
      </Box>
    );
  }

  const { page, ownerProfile, services } = data;
  const ownerUser = ownerProfile?.user;

  // Calificación de la página = promedio ponderado de los servicios listados (por nº de reseñas)
  const serviciosCalificados = (services || []).filter(s => s.ratingPromedio > 0 && s.ratingTotal > 0);
  const totalResenas = serviciosCalificados.reduce((acc, s) => acc + s.ratingTotal, 0);
  const ratingPagina = totalResenas > 0
    ? serviciosCalificados.reduce((acc, s) => acc + s.ratingPromedio * s.ratingTotal, 0) / totalResenas
    : 0;
  const bannerUrl = page.img ? `${urlImg}/paginas/${page._id}/${page.img}` : 'https://images.unsplash.com/photo-1555507036-ab1f4038808a?q=80&w=1926&auto=format&fit=crop';

  // Resolve owner avatar
  let ownerAvatarUrl = "";
  if (ownerUser?.avatar) {
    ownerAvatarUrl = ownerUser.avatar.startsWith('http')
      ? ownerUser.avatar
      : `${urlImg}/users/${ownerUser._id}/${ownerUser.avatar}`;
  }

  const isOwner = authUser && ownerUser && String(authUser._id) === String(ownerUser._id);

  // Vista previa: la página (o alguno de sus servicios) todavía no es pública. Solo la ven su dueño y las personas invitadas,
  // que así pueden revisar a qué las invitan antes de aceptar.
  const vistaPrevia = data.vistaPrevia || null;
  const meEsperan = !isOwner && (services || []).some(s => s.enEspera === 'pendiente');
  const avisoVistaPrevia = !vistaPrevia ? null
    : vistaPrevia.soyDueno
      ? 'Vista previa de tu página. Los servicios marcados aún no los ve el público: falta que acepte quien los va a impartir.'
      : meEsperan
        ? 'Te invitaron a esta página. Los servicios marcados esperan tu respuesta: el público no los verá hasta que aceptes.'
        : `Vista previa${vistaPrevia.publicada ? '' : ': esta página todavía no está publicada'}.`;

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', pb: 8, pt: 4, mt: 10 }}>
      <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2, md: 4 } }}>
        {avisoVistaPrevia && (
          <Alert
            severity="warning"
            sx={{ mb: 2, mx: { xs: 2, sm: 0 }, borderRadius: '12px' }}
            action={meEsperan && (
              <Button color="inherit" size="small" onClick={() => navigate('/user/colaboraciones')} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                Responder invitación
              </Button>
            )}
          >
            {avisoVistaPrevia}
          </Alert>
        )}
        <PageBanner
          title={page.nombre}
          image={bannerUrl}
        />

        <PageSummary
          title={page.nombre}
          rating={ratingPagina}
          ownerAvatar={ownerAvatarUrl}
          ownerName={`${ownerUser?.name || ""} ${ownerUser?.apellido || ""}`}
          ownerId={ownerUser?._id}
          isFavorite={isFavorite}
          onToggleFavorite={onToggleFavorite}
          onQuote={handleQuote}
          isOwner={isOwner}
        />

        <PageInfoBar
          categories={categoriaName}
          redes={page.redes}
        />

        <Box sx={{ mt: 6, px: { xs: 2, sm: 0 } }}>
          <Grid container spacing={5}>
            <Grid size={{ xs: 12, md: 4.5 }}>
              <PageAbout descripcion={page.descripcion} incluye={page.incluye} infoAdicional={page.infoAdicional} />
              {addressInfo && addressInfo.direccion && (
                <PageLocation
                  address={addressInfo.direccion}
                  placeId={addressInfo.place_id}
                  location={addressInfo.location}
                />
              )}
              <PageComments paginaId={pageId} />
            </Grid>

            <Grid size={{ xs: 12, md: 7.5 }}>
              <PageServiceList
                services={services}
                ownerName={page.nombre}
                irA={navigate}
                ownerId={ownerUser?._id}
                categories={categories}
                ownerAvatar={ownerAvatarUrl}
                isOwner={isOwner}
                pageId={pageId}
              />
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicPageDetail;
